/**
 * `<JoplinSyncPanel>` — Joplin connector card on the Sources tab.
 *
 * v4 P8 refit (2026-05-18, GAP_ANALYSIS_R2.md §5 / SO1-SO3):
 * the previous shadcn/Tailwind layout was a "debug-list" — long vertical
 * stack of every diagnostic field. The R2 review called this out as the
 * only meaningful spec divergence: a user landing on Sources wants
 * "what's connected, how healthy" at a glance, not pages of telemetry.
 *
 * U7 refit (2026-07-05): the one-off `.source-card` chrome is replaced by
 * the canonical `<SettingsCard>` (the `.s-card` grammar every Settings
 * panel uses) so Sources matches the General tab. Connection health moved
 * into the card's state pill; the 3 stats became a labeled
 * `.source-statgrid` (LAST SYNC / FAILURES / MODE as `.label-mono`
 * headers over aligned mono values); the "Diagnostics" disclosure and
 * "Poll now" action are unchanged in behavior.
 *
 * Data wiring unchanged: useJoplinStatusQuery (polled), useJoplinPollMutation.
 */

import { useState } from 'react';
import { useJoplinPollMutation, useJoplinStatusQuery, useSettingsQuery } from '@/hooks';
import { SettingsCard, type SettingsCardState } from '@/surfaces/settings/SettingsCard';

interface JoplinSyncPanelProps {
  active: boolean;
}

function relativeTime(iso: string | null): string {
  if (iso === null) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 14) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function JoplinSyncPanel({ active }: JoplinSyncPanelProps): JSX.Element {
  const statusQuery = useJoplinStatusQuery({ enabled: active });
  const pollMutation = useJoplinPollMutation();
  const settingsQuery = useSettingsQuery();
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);

  const writeBackEnabled =
    settingsQuery.data?.values['joplin_write_back_enabled'] === true;
  const connected = statusQuery.data?.connected ?? false;
  const failures = statusQuery.data?.consecutiveFailures ?? 0;
  const lastSyncLabel = relativeTime(statusQuery.data?.lastIncrementalSyncAt ?? null);
  const lastReconLabel = relativeTime(statusQuery.data?.lastReconciliationSweepAt ?? null);

  // The connected toggle: in v1 we don't have a backend disconnect endpoint,
  // so it's a read-only mirror of `status.connected`. Clicking opens a tooltip;
  // future PR wires this to a real connect/disconnect endpoint.
  const toggleTitle = connected
    ? 'Connected. Disconnect flow ships in v1.5 (re-pair via Settings → Joplin).'
    : 'Not connected. Set the Joplin token in Settings → API keys to connect.';

  // Card state pill = connection health at a glance (SettingsCard grammar).
  const state: SettingsCardState = !connected
    ? { kind: 'idle', label: 'Offline' }
    : failures === 0
    ? { kind: 'good', label: 'Connected' }
    : failures < 3
    ? { kind: 'warn', label: `Connected · ${failures} failing` }
    : { kind: 'bad', label: `Connected · ${failures} failing` };

  const failuresClass =
    failures === 0
      ? 'source-diagnostics__v'
      : failures < 3
      ? 'source-diagnostics__v source-diagnostics__v--warn'
      : 'source-diagnostics__v source-diagnostics__v--bad';

  return (
    <SettingsCard
      id="joplin"
      title={
        <>
          Joplin sync
          {writeBackEnabled && (
            <span className="chip chip--cool" title="D-007 write-back is enabled">
              write-back
            </span>
          )}
        </>
      }
      desc={
        connected
          ? 'Polling the local Joplin instance for incremental sync.'
          : 'Configure the Joplin token in Settings → API keys to start syncing.'
      }
      state={state}
    >
      <div className="source-connector-row">
        <div className="source-statgrid" aria-label="Joplin sync stats">
          <div className="source-statgrid__stat" title="Most recent incremental sync">
            <span className="label-mono">Last sync</span>
            <span className="source-statgrid__v">{lastSyncLabel}</span>
          </div>
          <div
            className="source-statgrid__stat"
            title="Consecutive sync failures since the last success"
          >
            <span className="label-mono">Failures</span>
            <span className="source-statgrid__v">{failures}</span>
          </div>
          <div className="source-statgrid__stat" title="Operational mode of the connector">
            <span className="label-mono">Mode</span>
            <span className="source-statgrid__v">{writeBackEnabled ? 'read+write' : 'read'}</span>
          </div>
        </div>

        <div className="source-connector-row__actions">
          <button
            type="button"
            className="btn btn--sm"
            disabled={pollMutation.isPending}
            onClick={() => pollMutation.mutate()}
            title="Trigger an immediate poll of the Joplin server"
          >
            {pollMutation.isPending ? 'Polling…' : 'Poll now'}
          </button>

          <button
            type="button"
            className="source-toggle"
            aria-pressed={connected}
            disabled
            title={toggleTitle}
            aria-label={connected ? 'Connector enabled' : 'Connector disabled'}
          />
        </div>
      </div>

      {/* Diagnostics disclosure — collapsed by default. Pushes the operational
          telemetry one click away so the card scans clean. */}
      <button
        type="button"
        className="source-diagnostics-toggle"
        aria-expanded={diagnosticsOpen}
        onClick={() => setDiagnosticsOpen((v) => !v)}
      >
        <span className="source-diagnostics-toggle__chev" aria-hidden="true">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M3 2l4 3-4 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        Diagnostics
      </button>

      {diagnosticsOpen && (
        <div className="source-diagnostics">
          <div className="source-diagnostics__field">
            <span className="source-diagnostics__k">Last incremental sync</span>
            <span className="source-diagnostics__v">{lastSyncLabel}</span>
          </div>
          <div className="source-diagnostics__field">
            <span className="source-diagnostics__k">Last reconciliation</span>
            <span className="source-diagnostics__v">
              {lastReconLabel}
              <small>D-008 sweep</small>
            </span>
          </div>
          <div className="source-diagnostics__field">
            <span className="source-diagnostics__k">Consecutive failures</span>
            <span className={failuresClass}>{failures}</span>
          </div>
          <div className="source-diagnostics__field">
            <span className="source-diagnostics__k">Status</span>
            <span className="source-diagnostics__v">
              {connected ? 'Connected' : 'Offline'}
              <small>{statusQuery.data?.lastError ? 'See last error below' : 'Healthy'}</small>
            </span>
          </div>

          {statusQuery.data?.lastError != null && (
            <div className="source-diagnostics__activity" role="status">
              <strong style={{ color: 'var(--warn)' }}>Last sync error:</strong>{' '}
              {statusQuery.data.lastError}
            </div>
          )}

          {/* Per-event activity isn't exposed by GET /sources/joplin/status yet —
              keep the copy honest rather than leaking the endpoint TODO into UI. */}
          <div className="source-diagnostics__activity">
            <strong>Recent activity:</strong> per-event sync history isn&apos;t recorded yet.
          </div>
        </div>
      )}
    </SettingsCard>
  );
}
